import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CartLine, CartService } from '../../core/services/cart.service';
import { CheckoutPricedLine, CheckoutService, CreateCheckoutSessionResponse, resolveStorefrontUrl } from '../../core/services/checkout.service';
import { OverlayService } from '../../core/services/overlay.service';
import { createIdempotencyKey } from '../../core/utils/id.util';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PriceChange {
  name: string;
  oldPrice: number;
  newPrice: number;
}

@Component({
  selector: 'app-checkout',
  imports: [RouterLink, ReactiveFormsModule, DecimalPipe],
  templateUrl: './checkout.component.html'
})
export class CheckoutComponent {
  private readonly cart = inject(CartService);
  private readonly checkout = inject(CheckoutService);
  private readonly overlay = inject(OverlayService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly items = this.cart.items;
  readonly subtotal = this.cart.subtotal;
  readonly tax = this.cart.tax;
  readonly total = this.cart.total;
  readonly taxRatePercent = this.cart.taxRatePercent;
  readonly isEmpty = computed(() => this.items().length === 0);

  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly priceChanges = signal<PriceChange[]>([]);
  readonly pendingSession = signal<CreateCheckoutSessionResponse | null>(null);

  private idempotencyKey: string | null = null;

  readonly contactForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    firstName: ['', [Validators.required, Validators.maxLength(150)]],
    lastName: ['', [Validators.required, Validators.maxLength(150)]],
    phone: ['', [Validators.required, Validators.maxLength(40)]]
  });

  constructor() {
    this.overlay.closeAll();
  }

  lineTotal(line: CartLine): number {
    return line.unitPrice * line.quantity;
  }

  confirmPriceChanges(): void {
    const session = this.pendingSession();
    if (!session) return;
    this.priceChanges.set([]);
    this.pendingSession.set(null);
    this.proceedToPayment(session);
  }

  dismissPriceChanges(): void {
    this.priceChanges.set([]);
    this.pendingSession.set(null);
    this.idempotencyKey = null;
  }

  pay(): void {
    if (this.isEmpty()) return;

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.error.set('Please fill in your billing details.');
      return;
    }
    if (this.items().some((line) => !GUID.test(line.id))) {
      this.error.set('The cart is not connected to the catalog. Refresh the shop and add the products again.');
      return;
    }

    this.submitting.set(true);
    this.error.set(null);
    this.priceChanges.set([]);

    if (!this.idempotencyKey) {
      this.idempotencyKey = createIdempotencyKey();
    }

    const contact = this.contactForm.getRawValue();
    this.checkout
      .createSession(
        {
          email: contact.email.trim(),
          firstName: contact.firstName.trim(),
          lastName: contact.lastName.trim(),
          phone: contact.phone.trim(),
          storefrontUrl: resolveStorefrontUrl(),
          lines: this.items().map((line) => ({
            type: 'product',
            id: line.id,
            quantity: line.quantity
          }))
        },
        this.idempotencyKey
      )
      .subscribe({
        next: (result) => {
          this.submitting.set(false);
          const changes = this.detectPriceChanges(result.lines);
          if (changes.length > 0) {
            this.priceChanges.set(changes);
            this.pendingSession.set(result);
          } else {
            this.proceedToPayment(result);
          }
        },
        error: (err) => {
          this.submitting.set(false);
          const apiError =
            typeof err?.error?.error === 'string'
              ? err.error.error
              : typeof err?.error === 'string'
                ? err.error
                : null;
          this.error.set(apiError ?? 'Could not start checkout. Please try again.');
        }
      });
  }

  private detectPriceChanges(serverLines: CheckoutPricedLine[]): PriceChange[] {
    const cartItems = this.items();
    const changes: PriceChange[] = [];
    for (const serverLine of serverLines) {
      const cartLine = cartItems.find((l) => l.id === serverLine.id.toLowerCase() || serverLine.id.toLowerCase() === l.id.toLowerCase());
      if (cartLine && Math.abs(cartLine.unitPrice - serverLine.unitPrice) > 0.001) {
        changes.push({
          name: serverLine.name,
          oldPrice: cartLine.unitPrice,
          newPrice: serverLine.unitPrice
        });
      }
    }
    return changes;
  }

  private proceedToPayment(result: CreateCheckoutSessionResponse): void {
    this.checkout.saveEmbeddedSession({
      sessionId: result.sessionId,
      clientSecret: result.clientSecret,
      publishableKey: result.publishableKey
    });
    void this.router.navigateByUrl('/checkout/pay');
  }
}
